import { Box, Button, CircularProgress, Stack } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ColonWrapper,
  CommonBoxWrapper,
  InviteStatusWrapper,
  LabelWrapper,
  ResendInvitationWrapper,
  ValueWrapper,
} from './styled-components';

import { useLocalizedDepartmentData } from '../../../../../hooks/fetcher/useDepartmentData';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import userService from '../../../../../services/userService';
import ResendIcon from '../../../../icons/resendIcon';

type InviteStatus = 'Pending' | 'Expired' | 'Accepted';

type Role = {
  type: string;
  value: number;
};

interface UserExtraDetailsProps {
  slug: string;
  userId: number;
  getInviteStatusStyle: (status: InviteStatus) => {
    width: string;
    borderRadius: string;
    textAlign: string;
    color: string;
    paddingTop: string;
    paddingBottom: string;
    backgroundColor: string;
  };
  allRoles: Array<Role>;
  roleId: number;
  departmentName: string;
}

const UserExtraDetails = ({
  slug,
  userId,
  getInviteStatusStyle,
  departmentName,
  roleId,
  allRoles,
}: UserExtraDetailsProps) => {
  const intl = useIntl();
  const [hasLoaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [roleName, setRoleName] = useState<string>('');
  const [hasFetchRoleSettings, setHasFetchRoleSettings] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const { createToastNotification } = useToastNotificationContext();
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();
  const [extraDetail, setExtraDetail] = useState<{
    email: string;
    inviteStatus: InviteStatus;
    expiryPeriod: number;
  }>({
    email: '',
    inviteStatus: 'Pending',
    expiryPeriod: 0,
  });

  const resendInvitation = () => {
    if (!resendLoading) {
      setResendLoading(true);
      userService
        .postResendInvitation(userId)
        .then(() => {
          setResendLoading(false);
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Email has been sent successfully.',
            }),
            'success',
          );
        })
        .catch((e) => {
          setResendLoading(false);
          if (typeof e.response.data.message !== 'undefined')
            createToastNotification(e.response.data.message, 'error');
        });
    }
  };

  const getUserRoleSettings = useCallback(() => {
    if (!hasFetchRoleSettings) {
      if (allRoles.length > 0) {
        let roleType = _.find(allRoles, {
          value: roleId,
        })?.type;
        roleType = typeof roleType !== 'undefined' ? roleType : '';
        if (roleType === 'Tenant') roleType = 'User';
        else if (roleType === 'Tenant Admin') roleType = 'Admin';

        setRoleName(roleType);
      }

      userService
        .getUserRoleSettings(slug, userId)
        .then((response) => {
          const { email, ...extraProps } = extraDetail;

          setExtraDetail({
            ...extraProps,
            email: response.data.email,
            inviteStatus: response.data.user_settings_invite_status,
            expiryPeriod: response.data.invite_link_hours_remaining,
          });

          setLoading(false);
          setHasFetchRoleSettings(true);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [allRoles, extraDetail, roleId, slug, userId, hasFetchRoleSettings]);

  useEffect(() => {
    if (!hasLoaded) setLoaded(true);
    if (hasLoaded) getUserRoleSettings();
  }, [hasLoaded, getUserRoleSettings]);

  return (
    <Stack direction='row' sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
        <CommonBoxWrapper>
          <LabelWrapper sx={{ width: '30%' }}>
            <FormattedMessage defaultMessage='Email' />
          </LabelWrapper>
          <ColonWrapper>:</ColonWrapper>
          <ValueWrapper sx={{ width: '57%', paddingLeft: '12px' }}>
            <div>
              {loading ? (
                <CircularProgress sx={{ color: '#181818' }} size={20} />
              ) : (
                extraDetail.email
              )}
            </div>
          </ValueWrapper>
        </CommonBoxWrapper>
        <CommonBoxWrapper>
          <LabelWrapper sx={{ width: '30%' }}>
            <FormattedMessage defaultMessage='Department' />
          </LabelWrapper>
          <ColonWrapper>:</ColonWrapper>
          <ValueWrapper sx={{ width: '57%', paddingLeft: '12px' }}>
            {getLocalizedDepartmentName(departmentName)}
          </ValueWrapper>
        </CommonBoxWrapper>
        <CommonBoxWrapper>
          <LabelWrapper sx={{ width: '30%' }}>
            <FormattedMessage defaultMessage='User Role' />
          </LabelWrapper>
          <ColonWrapper>:</ColonWrapper>
          <ValueWrapper sx={{ width: '57%', paddingLeft: '12px' }}>
            {roleName}
          </ValueWrapper>
        </CommonBoxWrapper>
      </Box>
      <Box sx={{ width: '8%' }}></Box>
      <Box sx={{ width: '42%' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            mb: '12px',
          }}
        >
          <InviteStatusWrapper sx={{ mr: 2 }}>
            <FormattedMessage defaultMessage='Invite Status' />
          </InviteStatusWrapper>
          {!loading ? (
            <Box
              sx={{
                ...getInviteStatusStyle(extraDetail.inviteStatus),
                width: 'auto',
                padding: '4px 8px',
              }}
            >
              {extraDetail.inviteStatus}
            </Box>
          ) : (
            <CircularProgress
              color='primary'
              sx={{ color: '#181818' }}
              size={20}
            />
          )}
        </Box>
        {!loading && extraDetail.inviteStatus !== 'Accepted' && (
          <>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                mb: '12px',
              }}
            >
              <LabelWrapper sx={{ color: '#4F4F4F', mr: 2 }}>
                <FormattedMessage defaultMessage='Invitation Expiry Period:' />
              </LabelWrapper>
              <Box sx={{ display: 'flex', flexDirection: 'row', pl: 2 }}>
                <ValueWrapper sx={{ mr: 2, fontWeight: 500 }}>
                  {extraDetail.expiryPeriod}
                </ValueWrapper>
                <LabelWrapper sx={{ alignItems: 'flex-end' }}>
                  <FormattedMessage defaultMessage='Hours' />
                </LabelWrapper>
              </Box>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
              <LabelWrapper sx={{ color: '#4F4F4F', mr: 2 }}>
                <FormattedMessage defaultMessage='Expiry Period Extension:' />
              </LabelWrapper>
              <Box>
                <Button
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    backgroundColor: '#F0F0F0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                    }}
                  >
                    {resendLoading ? (
                      <CircularProgress
                        color='primary'
                        sx={{ color: '#181818' }}
                        size={20}
                      />
                    ) : (
                      <ResendIcon />
                    )}
                  </Box>
                  <ResendInvitationWrapper onClick={resendInvitation}>
                    <FormattedMessage defaultMessage='Resend Invitation' />
                  </ResendInvitationWrapper>
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};
export default UserExtraDetails;
